import React from "react";
import { Link } from "react-router-dom";
import SeoHead from "../../components/seo/SeoHead";
import SeoMarketingShell from "../../components/seo/SeoMarketingShell";
import {
  SEO_BASE_URL,
  internalLinkTargets,
} from "../../data/seoPages";

const GLASS_CARD =
  "rounded-3xl border border-[#E5E7EB] bg-white/95 backdrop-blur-2xl shadow-[0_24px_80px_rgba(15,23,42,0.12)]";
const GLASS_CARD_SOFT =
  "rounded-3xl border border-[#E5E7EB] bg-white/92 backdrop-blur-2xl shadow-[0_18px_60px_rgba(15,23,42,0.10)]";
const PRIMARY_CTA =
  "rounded-full px-7 py-3 text-white text-[12px] font-semibold tracking-[0.12em] uppercase bg-gradient-to-r from-[#FF4D4D] via-[#FF6B6B] to-[#FF4D4D] shadow-[0_14px_40px_rgba(248,113,113,0.45)] hover:shadow-[0_18px_54px_rgba(248,113,113,0.55)] transition-all";
const SECONDARY_CTA =
  "rounded-full px-7 py-3 text-[#111827] text-[12px] font-semibold tracking-[0.08em] uppercase border border-[#E5E7EB] bg-white hover:bg-[#F9FAFB] transition-all";

function Section({ id, title, children }) {
  return (
    <section id={id} className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
      <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
        {title}
      </h2>
      <div className="mt-4 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
        {children}
      </div>
    </section>
  );
}

function LocationCityPage({ cityConfig }) {
  const route = `/locations/${cityConfig.city}/airbnb-grocery-delivery`;
  const title = `${cityConfig.displayName} Airbnb Grocery Delivery | Pre-Arrival Grocery Stocking | Bellhopt`;
  const description =
    cityConfig.metaDescription ||
    `Bellhopt Concierge provides pre-arrival groceries that are delivered, stocked, and organized in ${cityConfig.displayName}. 24-hour lead time required.`;
  const canonical = `${SEO_BASE_URL}${route}/`;
  const faqItems = cityConfig.localFaq || [];

  const structuredData = [
    {
      "@context": "https://schema.org",
      "@type": "WebPage",
      name: title,
      url: canonical,
      description,
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: "Locations",
          item: `${SEO_BASE_URL}/locations/`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: cityConfig.displayName,
          item: canonical,
        },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: faqItems.map((item) => ({
        "@type": "Question",
        name: item.q,
        acceptedAnswer: {
          "@type": "Answer",
          text: item.a,
        },
      })),
    },
  ];

  return (
    <SeoMarketingShell>
      <SeoHead
        title={title}
        description={description}
        canonical={`${route}/`}
        robots={cityConfig.indexable ? "index,follow" : "noindex,follow"}
        ogTitle={`${cityConfig.displayName} Airbnb Grocery Delivery | Bellhopt Concierge`}
        ogDescription={description}
        structuredData={structuredData}
      />

      <main className="w-[90%] max-w-[1100px] m-auto pt-24 pb-10 mobile:w-[95%]">
        <section className={`${GLASS_CARD} p-8 mobile:p-5`}>
          <h1 className="font-segoe font-semibold text-[42px] mobile:text-[30px] text-[#111827] leading-tight">
            {cityConfig.displayName} Airbnb Grocery Delivery And Concierge Services
          </h1>
          <p className="mt-4 text-[20px] mobile:text-[17px] text-[#6B7280] leading-8">
            {cityConfig.heroIntro}
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>
              Get Started With Bellhopt
            </a>
            <Link to="/for/property-managers" className={SECONDARY_CTA}>
              For Property Managers
            </Link>
          </div>
          <p className="mt-4 text-[13px] text-[#6B7280]">
            Guests shop 20,000+ products. Bellhopt Concierge delivers, stocks, and organizes before
            check-in. 24-hour lead time required.
          </p>
        </section>

        <section className={`mt-6 ${GLASS_CARD_SOFT} p-6 mobile:p-4`}>
          <h2 className="font-segoe font-semibold text-[22px] text-[#111827]">On This Page</h2>
          <div className="mt-3 flex flex-wrap gap-3">
            {[
              ["what-it-means", `What Grocery Stocking Means In ${cityConfig.displayName}`],
              ["how-it-works", "How It Works"],
              ["concierge", "During-Stay Concierge Services"],
              ["property-managers", "Why Property Managers Use Bellhopt"],
              ["arrival-orders", "Popular Arrival Orders"],
              ["lead-time", "24-Hour Lead Time"],
              ["faq", "Local FAQ"],
            ].map(([id, label]) => (
              <a
                key={id}
                href={`#${id}`}
                className="px-4 py-2 rounded-full border border-[#E5E7EB] text-[#111827] bg-white hover:bg-[#F9FAFB] transition-all"
              >
                {label}
              </a>
            ))}
          </div>
        </section>

        <div className="mt-6 space-y-6">
          <Section
            id="what-it-means"
            title={`What Pre-Arrival Grocery Delivery Means In ${cityConfig.displayName}`}
          >
            <p>
              Bellhopt Concierge is not a door-only grocery app. Guests place a pre-arrival grocery
              order from 20,000+ products, and Bellhopt delivers, stocks, and organizes the home so
              the kitchen feels ready from the first hour of the stay.
            </p>
            <p className="mt-4">{cityConfig.marketWhy}</p>
            <ul className="list-disc pl-6 mt-4">
              <li>Pre-arrival grocery delivery built for short-term rentals, not generic last-mile drop-off</li>
              <li>Clean fridge, pantry, and countertop organization for an arrival-ready first impression</li>
              <li>A higher-end guest experience that gives property managers a more repeatable service standard</li>
            </ul>
          </Section>

          <Section id="how-it-works" title={`How Bellhopt Concierge Works In ${cityConfig.displayName}`}>
            <ol className="list-decimal pl-6 space-y-4">
              {cityConfig.howItWorksNotes.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ol>
            <div className="mt-6 rounded-2xl border border-[#E5E7EB] p-5 bg-[#F9FAFB]/90">
              <h3 className="font-segoe font-semibold text-[24px] mobile:text-[20px] text-[#111827]">
                Operations Reality In {cityConfig.displayName}
              </h3>
              <p className="mt-2 text-[#6B7280] leading-8">{cityConfig.operationsNotes}</p>
            </div>
          </Section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
              Make Arrival Effortless In {cityConfig.displayName}
            </h2>
            <p className="mt-4 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
              Pre-arrival groceries, stocked and organized by Bellhopt Concierge, plus a stronger
              guest-service layer before and during the stay.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>
                Get Started With Bellhopt
              </a>
              <Link to="/for/property-managers" className={SECONDARY_CTA}>
                For Property Managers
              </Link>
            </div>
          </section>

          <Section
            id="concierge"
            title={`During-Stay Concierge Services In ${cityConfig.displayName}`}
          >
            <p>
              Bellhopt is more than a grocery stocking service. In the right property and market
              setup, the same Bellhopt Concierge layer can support guest concierge services during
              the stay, helping operators deliver a more premium and responsive experience.
            </p>
            <ul className="list-disc pl-6 mt-4">
              {cityConfig.conciergeUseCases.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </Section>

          <Section
            id="property-managers"
            title={`Why Property Managers Use Bellhopt In ${cityConfig.displayName}`}
          >
            <p>
              Property managers do not need another guest promise that creates more operational
              overhead. They need a process that is dispatchable, repeatable, and aligned to the way
              short-term rentals actually run in {cityConfig.displayName}.
            </p>
            <ul className="list-disc pl-6 mt-4">
              {cityConfig.serviceHighlights.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </Section>

          <Section id="arrival-orders" title={`Popular Arrival Orders In ${cityConfig.displayName}`}>
            <p>
              {`These are the kinds of pre-arrival grocery orders that work well in ${cityConfig.displayName}. They are not restrictive packages. Guests can still shop from 20,000+ products and customize the order to fit the trip.`}
            </p>
            <ul className="list-disc pl-6 mt-4">
              {cityConfig.arrivalBundle.map((bundleItem) => (
                <li key={bundleItem}>{bundleItem}</li>
              ))}
            </ul>
          </Section>

          <Section id="lead-time" title={`The 24-Hour Lead Time Policy In ${cityConfig.displayName}`}>
            <p>
              Bellhopt requires a minimum 24-hour lead time for pre-arrival grocery delivery and
              grocery stocking orders. That lead time protects product quality, routing reliability,
              property access coordination, and the final stocked-and-organized result.
            </p>
            <p className="mt-4">
              If the goal is a premium arrival, the process needs enough time to execute cleanly.
              That is why Bellhopt is built around reliability, not rushed same-day workarounds.
            </p>
          </Section>

          <Section id="faq" title={`Local FAQ For ${cityConfig.displayName}`}>
            <div className="space-y-3">
              {faqItems.map((item) => (
                <details
                  key={item.q}
                  className="rounded-2xl border border-[#E5E7EB] p-4 bg-white/90 open:bg-[#F9FAFB]"
                >
                  <summary className="cursor-pointer font-segoe font-semibold text-[18px] text-[#111827]">
                    {item.q}
                  </summary>
                  <p className="mt-3 text-[16px] text-[#6B7280] leading-7">{item.a}</p>
                </details>
              ))}
            </div>
          </Section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
              Related Bellhopt Pages
            </h2>
            <p className="mt-4 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
              {`Explore the broader Bellhopt SEO pages that support this ${cityConfig.displayName} city page, including pre-arrival groceries, vacation rental grocery delivery, property manager use cases, and integrations.`}
            </p>
            <div className="mt-5 flex flex-wrap gap-3">
              {internalLinkTargets.map((item) => (
                <Link key={item.to} to={item.to} className={SECONDARY_CTA}>
                  {item.label}
                </Link>
              ))}
            </div>
          </section>

          <section className={`${GLASS_CARD_SOFT} p-7 mobile:p-5`}>
            <h2 className="font-segoe font-semibold text-[30px] mobile:text-[24px] text-[#111827]">
              {`Ready To Raise The Arrival Standard In ${cityConfig.displayName}?`}
            </h2>
            <p className="mt-4 text-[18px] mobile:text-[16px] text-[#6B7280] leading-8">
              Give guests a stocked, organized home before they walk in and give your team a more
              reliable concierge workflow behind the scenes.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="https://host.bellhopt.com/signup" className={PRIMARY_CTA}>
                Get Started
              </a>
              <a href="mailto:sales@bellhopt.com" className={SECONDARY_CTA}>
                Talk To Sales
              </a>
            </div>
          </section>
        </div>
      </main>
    </SeoMarketingShell>
  );
}

export default LocationCityPage;
